// API client for Pollygram Mini App

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Get Telegram user data from WebApp initData
function getTelegramUser(): { id: string; username?: string; photoUrl?: string } | null {
  if (typeof window === "undefined" || !window.Telegram?.WebApp) {
    // Fallback for development outside Telegram
    console.log("[Auth] No Telegram WebApp, using dev fallback");
    return { id: "dev-user-123", username: "dev_user" };
  }

  const webApp = window.Telegram.WebApp;
  const initData = webApp.initData;
  const initDataUnsafe = webApp.initDataUnsafe;
  const user = initDataUnsafe?.user;

  console.log("[Auth] initData:", initData ? `${initData.substring(0, 50)}...` : "empty");
  console.log("[Auth] initDataUnsafe:", JSON.stringify(initDataUnsafe));
  console.log("[Auth] user:", JSON.stringify(user));

  if (!user) {
    // If we're in Telegram but no user, try to parse initData manually or use a fallback
    console.warn("[Auth] No user in initDataUnsafe, WebApp platform:", webApp.platform);
    return null;
  }

  return {
    id: user.id.toString(),
    username: user.username,
    photoUrl: user.photo_url,
  };
}

// Build headers with Telegram auth
function getHeaders(): HeadersInit {
  const user = getTelegramUser();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (user) {
    headers["x-telegram-id"] = user.id;
    if (user.username) {
      headers["x-telegram-username"] = user.username;
    }
    if (user.photoUrl) {
      headers["x-telegram-photo-url"] = user.photoUrl;
    }
  }

  return headers;
}

// API response types
export interface User {
  id: string;
  telegramId: string;
  telegramUsername: string | null;
  telegramPhotoUrl: string | null;
  walletAddress: string | null;
  balance: number;
}

export type ChallengeMode = "BINARY" | "VERSUS" | "CUSTOM";

export interface ChallengeSide {
  label: string;
  total: number;
  participants: Array<{ username: string; photoUrl: string | null; amount: number }>;
}

export interface Position {
  id: string;
  side: "YES" | "NO";
  amount: string;
  vote: string | null;
  user: User;
}

export interface Challenge {
  id: string;
  description: string;
  type: ChallengeMode;
  status: "OPEN" | "LOCKED" | "RESOLVING" | "SETTLED" | "REFUNDED" | "EXPIRED";
  poolTotal: string;
  outcome: string | null;
  chatId: string;
  messageId: string | null;
  createdAt: string;
  closeTime: string | null;
  resolutionTime: string | null;
  durationMinutes: number | null;
  lockedAt: string | null;
  timerEndsAt: string | null;
  creator: User;
  // Raw positions data
  positions?: Position[];
  // Labels for display
  labelA: string;
  labelB: string;
  // New format with labels
  sideA: ChallengeSide;
  sideB: ChallengeSide;
  // Backwards compatibility
  yesSide: {
    total: number;
    participants: Array<{ username: string; photoUrl: string | null; amount: number }>;
  };
  noSide: {
    total: number;
    participants: Array<{ username: string; photoUrl: string | null; amount: number }>;
  };
}

// API methods
export const api = {
  // Get current user
  async getMe(): Promise<{ user: User; demoMode: boolean }> {
    const res = await fetch(`${API_URL}/api/users/me`, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      throw new Error("Failed to fetch user");
    }
    return res.json();
  },

  // Create a challenge
  async createChallenge(data: {
    description: string;
    side?: "YES" | "NO"; // Optional for VERSUS mode
    stake: number;
    chatId: string;
    closeTime?: string;
    resolutionTime?: string;
    durationMinutes?: number;
    mode?: ChallengeMode;
    labelA?: string;
    labelB?: string;
  }): Promise<{ challenge: Challenge; userBalance: number | null }> {
    const res = await fetch(`${API_URL}/api/challenges`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to create challenge");
    }
    return res.json();
  },

  // Get challenge by ID
  async getChallenge(id: string): Promise<{
    challenge: Challenge;
    userVote: string | null;
    userSide: string | null;
    userStake: number;
    userWon: boolean | null;
    userEarnings: number;
  }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}`, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      throw new Error("Failed to fetch challenge");
    }
    return res.json();
  },

  // Join a challenge
  async joinChallenge(
    id: string,
    data: { side?: "YES" | "NO"; stake: number }
  ): Promise<{ challenge: Challenge; userBalance: number | null }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}/join`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to join challenge");
    }
    return res.json();
  },

  // Get user's challenges
  async getMyChallenges(): Promise<{ challenges: Challenge[]; balance: number }> {
    const res = await fetch(`${API_URL}/api/challenges/user/me`, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      throw new Error("Failed to fetch challenges");
    }
    return res.json();
  },

  // Get user's active challenges and stats for home screen
  async getActiveChallenges(): Promise<{
    challenges: Array<
      Challenge & {
        userSide: string | null;
        userVote: string | null;
        opponentUsername: string | null;
        opponentPhotoUrl: string | null;
        needsAction: boolean;
      }
    >;
    stats: {
      wins: number;
      losses: number;
      draws?: number;
      totalEarned: number;
    };
    balance: number;
  }> {
    const res = await fetch(`${API_URL}/api/challenges/user/active`, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      throw new Error("Failed to fetch active challenges");
    }
    return res.json();
  },

  // Cancel a challenge (creator only, before opponent joins)
  async cancelChallenge(id: string): Promise<{ success: boolean; message: string; userBalance: number | null }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}/cancel`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      let errorMessage = `Failed to cancel challenge: ${res.statusText || res.status}`;
      try {
        const error = await res.json();
        if (error.error) {
          errorMessage = error.error;
        }
      } catch {
        // JSON parsing failed, use default message
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },

  // Close (lock) a challenge
  async closeChallenge(id: string): Promise<{ challenge: Challenge }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}/close`, {
      method: "POST",
      headers: getHeaders(),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to close challenge");
    }
    return res.json();
  },

  // Start resolution
  async startResolution(id: string): Promise<{ challenge: Challenge }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}/start-resolution`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to start resolution");
    }
    return res.json();
  },

  // Submit vote
  async vote(
    id: string,
    vote: "YES" | "NO"
  ): Promise<{
    challenge: Challenge;
    resolution: "SETTLED" | "REFUNDED" | "PENDING";
    votesReceived: number;
    totalParticipants: number;
  }> {
    const res = await fetch(`${API_URL}/api/challenges/${id}/vote`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ vote }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || "Failed to submit vote");
    }
    return res.json();
  },
};

export default api;
